import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Paid as PaidIcon,
  MoreVert as MoreVertIcon,
  PersonOutline as PersonIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Avatar,
  Tooltip,
  Button,
  Card,
  alpha,
  useTheme,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import GenericTable from "0.Mine/0.Components/Reusable/GenericTable";
import { Customer } from "0.Mine/3.Interfaces/customer";
import { SafeArgonTypography, SafeArgonButton, SafeArgonBox } from "0.Mine/4.Argon/ArgonComponents";

import CustomerSendEmailModal from "./CustomerSendEmailModal";
interface BookingTableProps {
  bookings: Customer[];
  onRowClick: (booking: Customer) => void;
}

// Helper Components
const SearchBar: React.FC<{
  searchTerm: string;
  onSearchChange: (value: string) => void;
}> = ({ searchTerm, onSearchChange }) => {
  return (
    <TextField
      variant="outlined"
      size="small"
      placeholder="Cerca prenotazione..."
      value={searchTerm}
      onChange={(e) => onSearchChange(e.target.value)}
      sx={{ minWidth: 300 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: searchTerm ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="clear search"
              onClick={() => onSearchChange("")}
              edge="end"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

const ServiceList: React.FC<{ services: string[] }> = ({ services }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!services.length) return <SafeArgonTypography variant="body2">-</SafeArgonTypography>;

  if (services.length <= 2) {
    return (
      <SafeArgonBox sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {services.map((service, index) => (
          <Chip
            key={index}
            label={service}
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 500,
            }}
          />
        ))}
      </SafeArgonBox>
    );
  }

  return (
    <SafeArgonBox sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      <Chip
        label={services[0]}
        size="small"
        sx={{
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
          fontWeight: 500,
        }}
      />
      <Tooltip
        title={
          <Box sx={{ p: 1 }}>
            {services.slice(1).map((service, index) => (
              <SafeArgonTypography key={index} variant="body2">
                {service}
              </SafeArgonTypography>
            ))}
          </Box>
        }
      >
        <Chip
          label={`+${services.length - 1}`}
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
            color: theme.palette.secondary.main,
            fontWeight: 500,
            cursor: "pointer",
          }}
        />
      </Tooltip>
    </SafeArgonBox>
  );
};

const CustomerAvatar: React.FC<{ name: string; pic: string }> = ({ name, pic }) => {
  return pic ? (
    <Avatar src={pic} alt={name} sx={{ width: 40, height: 40 }} />
  ) : (
    <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
      {name.charAt(0).toUpperCase()}
    </Avatar>
  );
};

// Main component
export const BookingTable: React.FC<BookingTableProps> = ({ bookings, onRowClick }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [messageModalOpen, setMessageModalOpen] = useState(false);

  // State for action menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBooking, setSelectedBooking] = useState<Customer | null>(null);

  // Menu open/close handlers

  const handleMessageModalClosed = () => {
    setMessageModalOpen(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, booking: Customer) => {
    event.stopPropagation(); // Prevent row click
    setMenuAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedBooking(null);
  };

  // Action handlers
  const handleSendEmail = (booking: Customer) => {
    console.log("Send email to:", booking.email);
    setMessageModalOpen(true);
    handleMenuClose();
  };

  const { t } = useTranslation();

  const handleSendWhatsApp = (booking: Customer) => {
    if (booking.phoneNumber) {
      // Format phone number for WhatsApp (remove spaces, dashes, etc.)
      const formattedPhone = booking.phoneNumber.replace(/\D/g, "");
      console.log("Send WhatsApp to:", formattedPhone);
      // Open WhatsApp web or app with the phone number
      window.open(`https://wa.me/${formattedPhone}`, "_blank");
    }
    handleMenuClose();
  };

  // Helper functions
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    try {
      return format(parseISO(timeString), "h:mm a");
    } catch (error) {
      return timeString;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Filter logic
  const filteredBookings = useMemo(() => {
    if (!searchTerm.trim()) return bookings;

    const searchLower = searchTerm.toLowerCase();
    return bookings.filter(
      (booking) =>
        booking.name.toLowerCase().includes(searchLower) ||
        booking.surname.toLowerCase().includes(searchLower) ||
        booking.email.toLowerCase().includes(searchLower) ||
        (booking.phoneNumber && booking.phoneNumber.toLowerCase().includes(searchLower)) ||
        booking.services.some((service) => service.toLowerCase().includes(searchLower)) ||
        booking.booking_id.toString().includes(searchLower) ||
        booking.client_id.toString().includes(searchLower)
    );
  }, [bookings, searchTerm]);

  // Pagination logic
  const paginatedBookings = useMemo(() => {
    return filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredBookings, page, rowsPerPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <SafeArgonBox sx={{ width: "100%" }}>
      {/* Header with search and filters */}
      <SafeArgonBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <SafeArgonTypography variant="h6" fontWeight="bold" color="primary">
          {t("Gestione prenotazioni")}
        </SafeArgonTypography>
        <SafeArgonBox sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
          <Tooltip title="Advanced filters">
            <IconButton color="primary" aria-label="filter list">
              <FilterIcon />
            </IconButton>
          </Tooltip>
        </SafeArgonBox>
      </SafeArgonBox>

      <SafeArgonBox
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        <GenericTable
          data={bookings}
          columns={["name", "surname", "phoneNumber", "email", "booking_date", "totalPaid"]}
          columnLabels={{
            name: "Nome",
            surname: "Cognome",
            phoneNumber: "Telefono",
            email: "Email",
            booking_date: "Data prenotazione",
            totalPaid: "Prezzo totale",
          }}
          pagination={{
            page: 0,
            pageSize: 10,
            total: bookings.length,
            onPageChange: () => {},
            onRowsPerPageChange: () => {},
          }}
          noDataMessage="Nessuna prenotazione trovata"
          rowActions={[
            {
              name: "Dettagli",
              onClick: (row) => onRowClick(row as Customer),
            },
          ]}
        />

        {/* Table */}
        {/*<SafeArgonBox
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden'
        }}
      >
        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader aria-label="booking table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <SafeArgonBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    {t("Appuntamento")}
                  </SafeArgonBox>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Servizi</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  <SafeArgonBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                    <PaidIcon fontSize="small" color="action" />
                    {t("Prezzo totale")}
                  </SafeArgonBox>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t("Azioni")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBookings.length > 0 ? (
                paginatedBookings.map((booking) => (
                  <TableRow
                    hover
                    key={booking.booking_id}
                    onClick={() => onRowClick(booking)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <TableCell>
                      <SafeArgonBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CustomerAvatar name={`${booking.name} ${booking.surname}`} pic={booking.pic} />
                        <SafeArgonBox>
                          <Typography variant="body1" fontWeight={500}>
                            {booking.name} {booking.surname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.email}
                          </Typography>
                        </SafeArgonBox>
                      </SafeArgonBox>
                    </TableCell>
                    <TableCell>
                      <SafeArgonBox>
                        <SafeArgonBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatDate(booking.booking_date)}
                          </Typography>
                        </SafeArgonBox>
                        {booking.serviceDetails && booking.serviceDetails.length > 0 && (
                          <SafeArgonBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {formatTime(booking.serviceDetails[0].details.time_start)} - {formatTime(booking.serviceDetails[0].details.time_end)}
                            </Typography>
                          </SafeArgonBox>
                        )}
                      </SafeArgonBox>
                    </TableCell>
                    <TableCell>
                      <ServiceList services={booking.services || []} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500} color={booking.totalPaid > 0 ? 'success.main' : 'text.primary'}>
                        {formatCurrency(booking.totalPaid)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, booking)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      {t("Non ci sono prenotazioni")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination 
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredBookings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </SafeArgonBox>*/}

        {/* Action Menu 
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180,
            borderRadius: 2,
            overflow: 'visible',
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => selectedBooking && handleSendEmail(selectedBooking)}
          sx={{
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <EmailIcon fontSize="small" />
          <Typography variant="body2">{t("customerTable.sendEmail")}</Typography>
        </MenuItem>

        {/* WhatsApp option - conditionally rendered if phone number exists
        {selectedBooking && selectedBooking.phoneNumber && (
          <MenuItem
            onClick={() => selectedBooking && handleSendWhatsApp(selectedBooking)}
            sx={{
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <WhatsAppIcon fontSize="small" color="success" />
            <Typography variant="body2">{t("customerTable.sendWhatsapp")}</Typography>
          </MenuItem>
        )}
      </Menu>*/}

        <CustomerSendEmailModal
          open={messageModalOpen}
          onClose={handleMessageModalClosed}
          onSend={(data) => {
            console.log("Email sent:", data);
            handleMessageModalClosed();
          }}
        />
      </SafeArgonBox>
    </SafeArgonBox>
  );
};
